import { useToast } from '@chakra-ui/react';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { useAxios } from 'context/AxiosContextProvider';
import { manageErrorResponse } from 'helpers/manageErrorResponse';
import { useEffect } from 'react';

import config from '../config';

export enum PaymentInterval {
  YEARLY = 'year',
  MONTHLY = 'month',
}
export interface ISubscriptionPlan {
  id: string;
  name: string;
  description: string;
  recurring_charge: IRecurringCharge | number;
  included_addons: string;
  available_addons: ISubscriptionAddon[];
  unavailableFeatures: IFeature[];
  yearly_service_fee: number;
  charges: ICharge[];
  promotional_credit: number;
}

export const enum CHARGE_KEYS {
  UPDATE_MASTER_DATA = 'update_master_data',
  SERVICE_HISTORY_REQUEST = 'service_history_request',
  SERVICE_RECORD = 'service_record',
  UPDATE_SERVICE_RECORD = 'update_service_record',
  DELETE_SERVICE_RECORD = 'delete_service_record',
  FASTLANE = 'fastlane',
  SETUP_FEE = 'setup_fee',
}

export const enum ADDON_KEYS {
  IDSB_ADDON = 'idsb',
  CUSTOMER_CARE_ADDON = 'convenience_bundle',
  INSPECTION_PLANS_ADDON = 'inspection_plans',
}

export const enum PLAN_KEYS {
  PAY_PER_USE_PLAN = 'pay_per_use',
  PAY_PER_USE_PLAN_LEGACY = 'pay_per_use_legacy',
  OE_STARTER = 'oe_starter',
  OE_STARTER_LEGACY = 'oe_starter_legacy',
  OE_5 = 'oe_5',
  OE_5_LEGACY = 'oe_5_legacy',
  OE_10 = 'oe_10',
  OE_10_LEGACY = 'oe_10_legacy',
  OE_30 = 'oe_30',
  OE_30_LEGACY = 'oe_30_legacy',
  OE_ENTERPRISE_500 = 'oe_enterprise_500',
}

export interface IRecurringCharge {
  [PaymentInterval.MONTHLY]: { price_point_id: string; charge: number };
  [PaymentInterval.YEARLY]: { price_point_id: string; charge: number };
}

export interface IFeature {
  description: string;
}

export interface ICharge {
  id: string;
  charge: number;
}

export interface ISubscriptionAddon {
  id: string;
  name: string;
  description: string;
  price: {
    month: {
      amount: number;
    };
    year: {
      amount: number;
    };
  };
}

async function getSubscriptionPlans(axios: AxiosInstance) {
  const { data } = await axios.get(`${config.apiBaseUrl}/op/v1/subscription/plans`);
  return data;
}

export default function useSubscriptionPlanQuery() {
  const axios = useAxios();
  const toast = useToast();

  const query = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: getKey(),
    queryFn: () => getSubscriptionPlans(axios),
    notifyOnChangeProps: ['data', 'isFetching', 'error'],
    retry: false,
  });

  useEffect(() => {
    if (query.isError) {
      manageErrorResponse(toast, query.error as any);
    }
  }, [query.error, query.isError, toast]);

  return query;
}

export function getKey() {
  return ['Subscription'] as const satisfies QueryKey;
}
useSubscriptionPlanQuery.getKey = getKey;
